import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PublicLayout } from '../components/layouts/Layout'
import { Card, Button } from '../components/common/UI'
import { Download, Smartphone, ArrowRight, ExternalLink } from 'lucide-react'

const MobileScreenshots = () => {
  const [currentImage, setCurrentImage] = useState(0)
  
  const screenshots = [
    { title: "Events", image: "/assets/events-screenshot.png" },
    { title: "Trips", image: "/assets/trips-screenshot.png" },
    { title: "Find Friends", image: "/assets/friends-screenshot.png" },
    { title: "Welcome", image: "/assets/welcome-screenshot.png" }
  ]

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % screenshots.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [screenshots.length])

  return (
    <div className="relative flex justify-center lg:justify-end">
      <div className="relative">
        <div className="bg-white rounded-[40px] shadow-xl p-3 w-80 h-[600px] border border-gray-200">
          <div className="bg-gray-900 rounded-[32px] h-full flex flex-col overflow-hidden">
            <div className="flex justify-center pt-4 pb-2">
              <div className="w-32 h-5 bg-gray-800 rounded-full"></div>
            </div>
            <div className="flex-1 bg-white rounded-t-[28px] mt-1 overflow-hidden">
              <img src={screenshots[currentImage].image} alt={screenshots[currentImage].title} className="w-full h-full object-cover transition-opacity duration-500" />
            </div>
          </div>
        </div>
        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {screenshots.map((_, index) => (
            <button key={index} onClick={() => setCurrentImage(index)}
              className={`w-2 h-2 rounded-full transition-colors ${index === currentImage ? 'bg-blue-600' : 'bg-gray-300'}`} />
          ))}
        </div>
      </div>
    </div>
  )
}

export default function Share() {
  const [searchParams] = useSearchParams()
  const [shareData, setShareData] = useState<{ type: string; id: string } | null>(null)
  const [isAttemptingOpen, setIsAttemptingOpen] = useState(false)

  const getMobileOS = () => {
    const ua = navigator.userAgent.toLowerCase()
    if (/iphone|ipad|ipod/.test(ua)) return 'iOS'
    if (/android/.test(ua)) return 'Android'
    return 'Other'
  }

  const getDeepLink = (type: string, id: string) => {
    return `blasttimev4://${type}/${id}`
  }

  const getIntentLink = (type: string, id: string) => {
    return `intent://${type}/${id}#Intent;scheme=blasttimev4;package=com.xupoli.blasttimev4;S.browser_fallback_url=${encodeURIComponent(getStoreLink())};end`
  }

  const getStoreLink = () => {
    const os = getMobileOS()
    return os === 'iOS' 
      ? 'https://apps.apple.com/us/app/blasttime/id6739508048'
      : 'https://play.google.com/store/apps/details?id=com.xupoli.blasttimev4&hl=en'
  }

  useEffect(() => {
    const type = searchParams.get('type')
    const id = searchParams.get('id')
    
    if (type && id) {
      setShareData({ type, id })
    }
  }, [searchParams])

  const handleOpenApp = () => {
    if (!shareData) return
    
    const os = getMobileOS()
    setIsAttemptingOpen(true)
    
    if (os === 'Android') {
      window.location.href = getIntentLink(shareData.type, shareData.id)
    } else if (os === 'iOS') {
      const deepLink = getDeepLink(shareData.type, shareData.id)
      const start = Date.now()
      
      window.location.href = deepLink
      
      setTimeout(() => {
        if (Date.now() - start < 2000) {
          window.location.href = getStoreLink()
        }
        setIsAttemptingOpen(false)
      }, 1500)
    } else {
      window.location.href = getStoreLink()
    }
  }

  const handleDownload = () => {
    window.location.href = getStoreLink()
  }

  return (
    <PublicLayout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Join me on BlastTime!</h1>
            <p className="text-xl text-gray-600">Connect with friends and discover amazing experiences</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <MobileScreenshots />
            </div>
            <div className="space-y-8">
              <Card className="p-8">
                <h2 className="text-2xl font-bold mb-6">Get the BlastTime App</h2>
                <p className="text-gray-600 mb-6">Download BlastTime to connect with friends and create amazing memories.</p>
                
                {shareData && (
                  <Button 
                    onClick={handleOpenApp} 
                    disabled={isAttemptingOpen}
                    className="w-full py-4 text-lg font-semibold bg-green-600 hover:bg-green-700 mb-4 disabled:opacity-50"
                    icon={isAttemptingOpen ? undefined : ExternalLink}
                  >
                    {isAttemptingOpen ? 'Opening...' : 'Open in BlastTime'}
                  </Button>
                )}
                
                <Button 
                  onClick={handleDownload} 
                  className="w-full py-4 text-lg font-semibold bg-blue-600 hover:bg-blue-700" 
                  icon={Download}
                >
                  Download BlastTime
                </Button>
                
                <div className="flex items-center justify-center space-x-4 mt-4 text-sm text-gray-500">
                  <Smartphone className="w-4 h-4" />
                  <span>Available on iOS & Android</span>
                </div>
              </Card>
              <Card className="p-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                <h3 className="text-xl font-semibold mb-3">Why BlastTime?</h3>
                <div className="space-y-3 text-blue-100">
                  {['Connect with friends nearby', 'Discover local events', 'Join clubs and communities', 'Share experiences'].map((item, i) => (
                    <div key={i} className="flex items-center space-x-3">
                      <ArrowRight className="w-5 h-5" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  )
}